import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useApplicationById } from "hooks/useApplicationById";
import { useApplicationSchema } from "hooks/useApplicationSchema";
import { useAuth } from "utilities/auth";
import {
  isInternal,
  isPartner,
  needsPartnerAgentTerms,
} from "utilities/userType";
import { useToast } from "components/Toast";
import { makeApiRequest, apiRoutes } from "constants/apiRoutes";
import { useEffectiveCompanyId } from "hooks/useEffectiveCompanyId";
import { DownloadApplicationButton } from "components/Account/DownloadApplication";
import Skeleton from "components/Skeleton";
import PartnerAgentTermsModal from "components/PartnerAgentTermsModal";
import ApplicationStatusDialog from "components/ApplicationStatusDialog";
import AddDbaModal from "components/Companies/AddDbaModal";
import DbaRowEditor from "components/Companies/DbaRowEditor";
import RecordSections from "components/Registration/RecordSections";
import RegistrationTimeline from "components/Registration/RegistrationTimeline";
import RegistrationStatusPill from "components/Registration/RegistrationStatusPill";
import { orderStatusTransitions } from "utilities/registrationActions";
import ApplicationEditor from "./ApplicationEditor";
import {
  FileText,
  Calendar,
  ChevronDown,
  ChevronRight,
  Settings,
  CheckCircle,
  XCircle,
  AlertCircle,
  Edit3,
  Plus,
} from "lucide-react";
import EmptyState from "components/EmptyState";

const DRAFT_STATUSES = new Set(["new", "in_progress"]);

// MIRROR of sound-api config/statusMachines.mjs REGISTRATION_TRANSITIONS. The API
// is authoritative and re-validates every transition, so drift here shows up as a
// button that 400s. Keep the two in lockstep.
const REGISTRATION_TRANSITIONS = {
  new: ["in_progress"],
  in_progress: ["submitted"],
  submitted: ["approved", "rejected", "needs_info"],
  needs_info: ["submitted"],
  // SOUND-577: reopening a rejection routes through needs_info, NOT submitted —
  // same shape as the approved reopen below. This map is what decides which
  // buttons exist, so it is the half that has to change: the commit that
  // retargeted the state machine updated every CONSUMER of this edge
  // (isReopenTransition, actionConfigFor, both test files) and left this line on
  // the old target, which rendered "Mark Submitted" with no reason and 400'd on
  // click. Nothing here is covered by a test — the map lives in a JSX file and
  // sound-app has no component harness — which is the argument in SOUND-620.
  rejected: ["needs_info"],
  // SOUND-618: approved is no longer terminal — one edge out, to needs_info, so a
  // mistaken approval can be reopened from the UI instead of via a direct DB write.
  approved: ["needs_info"],
};

// `confirmTitle` exists because the button label is an imperative fragment that
// does not survive being dropped into a sentence — "Request Info this
// application?" — so each action carries its own grammatical heading.
//
// SOUND-643: every action is `btn-outline`, with the semantic colour carried by
// the ICON alone. Ticketing has no saturated fill buttons anywhere — its status
// control is an outline trigger whose colour lives in a small TicketStatusPill
// (see StatusControl in pages/tickets/Ticket.jsx). Three full-bleed green / red
// / amber blocks stacked in a 320px rail shouted louder than anything else on
// the page, including the record itself.
const STATUS_ACTION_CONFIG = {
  approved: {
    label: "Approve",
    confirmTitle: "Approve this application?",
    icon: CheckCircle,
    className: "btn-outline",
    iconClass: "text-success",
    requiresReason: false,
  },
  rejected: {
    label: "Reject",
    confirmTitle: "Reject this application?",
    icon: XCircle,
    className: "btn-outline",
    iconClass: "text-destructive",
    requiresReason: true,
  },
  needs_info: {
    label: "Request Info",
    confirmTitle: "Request more information?",
    icon: AlertCircle,
    className: "btn-outline",
    iconClass: "text-warning",
    requiresReason: true,
  },
  submitted: {
    label: "Mark Submitted",
    confirmTitle: "Mark this application as submitted?",
    icon: CheckCircle,
    className: "btn-outline",
    iconClass: "text-info",
    requiresReason: false,
  },
  in_progress: {
    label: "Start Review",
    confirmTitle: "Start reviewing this application?",
    icon: Settings,
    className: "btn-outline",
    iconClass: "text-info",
    requiresReason: false,
  },
};

// SOUND-618: the approved → needs_info edge surfaces a needs_info action on an
// APPROVED application, where a bare "Request Info" reads like a routine step
// rather than what it is — undoing an approval. Relabelled in that context only.
// Reason stays required, so every reopen is explained in the status history.
const REOPEN_ACTION = {
  label: "Reopen",
  confirmTitle: "Reopen this approved application?",
  icon: AlertCircle,
  className: "btn-outline",
  iconClass: "text-warning",
  requiresReason: true,
};

// SOUND-577: the same treatment for reopening a REJECTED application. It used
// to be `rejected → submitted` shown as "Mark Submitted" with NO reason — a
// rejection could be undone in one click leaving nothing in the status history
// explaining why, and it happened once in prod with an empty reason. The edge
// now routes through needs_info like the approved reopen, so the merchant also
// regains wizard access to fix what got them rejected.
const REOPEN_REJECTED_ACTION = {
  label: "Reopen",
  confirmTitle: "Reopen this rejected application?",
  icon: AlertCircle,
  className: "btn-outline",
  iconClass: "text-warning",
  requiresReason: true,
};

export default function ApplicationTab({ companyId }) {
  const navigate = useNavigate();
  const { application, loading, error, refetch } = useApplicationById(companyId);
  const { schema } = useApplicationSchema();
  const auth = useAuth();
  const effectiveCompanyId = useEffectiveCompanyId();
  const { toast } = useToast();
  const isAdmin = isInternal(auth.user);
  const partner = isPartner(auth.user);
  // SOUND-618: `pendingStatus` now drives a confirmation dialog for EVERY
  // transition. Reason + notification-suppression state live inside
  // ApplicationStatusDialog, which resets them on each open.
  const [pendingStatus, setPendingStatus] = useState(null);
  const [changingStatus, setChangingStatus] = useState(false);
  const [editing, setEditing] = useState(false);
  const [termsOpen, setTermsOpen] = useState(false);
  // Tracks which gated action to resume once partner T&C are accepted.
  // "edit" = open full editor; "addDba" = open AddDba modal.
  const [pendingGatedAction, setPendingGatedAction] = useState(null);
  const [addDbaOpen, setAddDbaOpen] = useState(false);
  const [dbasOpen, setDbasOpen] = useState(true);

  if (loading) {
    return (
      <div className="space-y-6">
        <Skeleton.Rect className="h-48 w-full" />
        <Skeleton.Rect className="h-48 w-full" />
        <Skeleton.Rect className="h-32 w-full" />
      </div>
    );
  }

  if (error || !application) {
    return (
      <EmptyState
        icon={FileText}
        heading="No application found for this company"
      />
    );
  }

  const formatDate = (timestamp) =>
    new Date(timestamp).toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });

  const currentStatus = application.status || "new";
  const validTransitions = REGISTRATION_TRANSITIONS[currentStatus] || [];
  const { forward: forwardTransitions, reopen: reopenTransitions } =
    orderStatusTransitions(currentStatus, validTransitions);

  // Resolves the action's presentation, swapping in the reopen wording when
  // needs_info is being reached FROM approved (see REOPEN_ACTION).
  const actionConfigFor = (targetStatus) => {
    if (currentStatus === "approved" && targetStatus === "needs_info") return REOPEN_ACTION;
    if (currentStatus === "rejected" && targetStatus === "needs_info") return REOPEN_REJECTED_ACTION;
    return STATUS_ACTION_CONFIG[targetStatus] || {
      label: targetStatus,
      className: "btn-secondary",
    };
  };

  const dbas = application.dbas || [];

  // SOUND-618: invoked by ApplicationStatusDialog on confirm. Every transition
  // now goes through the dialog — previously `approved` (requiresReason:false)
  // fired straight from the button click with no confirmation at all, despite
  // being terminal AND emailing the merchant.
  const handleStatusChange = async ({ reason, suppressNotifications }) => {
    const newStatus = pendingStatus;
    if (!newStatus) return;

    setChangingStatus(true);
    try {
      await makeApiRequest(
        "put",
        apiRoutes.changeApplicationStatus(companyId),
        auth.user,
        effectiveCompanyId,
        {
          status: newStatus,
          reason: reason || undefined,
          // Only send when true, so the request body is unchanged for the normal
          // notify-the-merchant path.
          ...(suppressNotifications ? { suppressNotifications: true } : {}),
        }
      );
      toast({
        status: "success",
        title: "Status updated",
        description: suppressNotifications
          ? `Status changed to ${newStatus}. The merchant was not notified.`
          : `Application status changed to ${newStatus}`,
      });
      setPendingStatus(null);
      await refetch();
    } catch (err) {
      toast({
        status: "destructive",
        title: "Failed to update status",
        description: err.response?.data?.error || "Unknown error",
      });
    } finally {
      setChangingStatus(false);
    }
  };

  const canEdit = (isAdmin || partner) && DRAFT_STATUSES.has(currentStatus);
  const canManageDbas = isAdmin || partner;
  // No cap: how many DBAs a merchant may have is an underwriting call (SOUND-649, SOUND-831).
  const canAddDba = canManageDbas;

  const handleEditClick = () => {
    if (needsPartnerAgentTerms(auth.user)) {
      setPendingGatedAction("edit");
      setTermsOpen(true);
      return;
    }
    setEditing(true);
  };

  const handleAddDbaClick = () => {
    if (needsPartnerAgentTerms(auth.user)) {
      setPendingGatedAction("addDba");
      setTermsOpen(true);
      return;
    }
    setAddDbaOpen(true);
  };

  // Row-level edit gate. Returns true when the caller can proceed; returns
  // false after queueing a T&C prompt so the caller can bail out and wait
  // for the user to accept.
  const handleDbaEditAttempt = () => {
    if (needsPartnerAgentTerms(auth.user)) {
      setPendingGatedAction("editDba");
      setTermsOpen(true);
      return false;
    }
    return true;
  };

  // The T&C modal invokes onAccept before onClose. We consume the queued
  // action here and reset the pointer.
  const handleTermsAccepted = () => {
    const action = pendingGatedAction;
    setPendingGatedAction(null);
    if (action === "edit") {
      setEditing(true);
    } else if (action === "addDba") {
      setAddDbaOpen(true);
    }
    // "editDba" has no auto-resume — the user clicks Edit again after accepting.
  };

  const handleTermsClose = () => {
    setTermsOpen(false);
    setPendingGatedAction(null);
  };

  // Edit-mode render — swap the view-only sections for the editor.
  if (editing) {
    return (
      <div>
        <ApplicationEditor
          companyId={companyId}
          application={application}
          onExit={() => setEditing(false)}
          onSaved={refetch}
        />
        <PartnerAgentTermsModal
          open={termsOpen}
          onClose={handleTermsClose}
          onAccept={handleTermsAccepted}
        />
      </div>
    );
  }

  return (
    <div>
      {/* The top action bar is gone. The status badge duplicated the Application
          Timeline's "Current Status", and the two buttons were actions sitting
          outside the Actions panel. Both moved into the sidebar. */}

      <PartnerAgentTermsModal
        open={termsOpen}
        onClose={handleTermsClose}
        onAccept={handleTermsAccepted}
      />

      {/* SOUND-618: single confirmation surface for every status transition. */}
      <ApplicationStatusDialog
        open={!!pendingStatus}
        targetStatus={pendingStatus}
        title={
          pendingStatus
            ? actionConfigFor(pendingStatus).confirmTitle ||
              `${actionConfigFor(pendingStatus).label} this application?`
            : ""
        }
        confirmLabel={pendingStatus ? actionConfigFor(pendingStatus).label : "Confirm"}
        statusLabel={pendingStatus}
        requiresReason={
          pendingStatus ? !!actionConfigFor(pendingStatus).requiresReason : false
        }
        // No longer true of `approved` now that it has a needs_info edge, so the
        // "cannot be changed back" warning correctly stops appearing.
        terminal={(REGISTRATION_TRANSITIONS[pendingStatus] || []).length === 0}
        canSuppress={isAdmin}
        submitting={changingStatus}
        onConfirm={handleStatusChange}
        onCancel={() => setPendingStatus(null)}
      />

      {addDbaOpen && (
        <AddDbaModal
          applicationId={companyId}
          onClose={() => setAddDbaOpen(false)}
          onCreated={async () => {
            setAddDbaOpen(false);
            await refetch();
          }}
          onTermsRequired={() => {
            setAddDbaOpen(false);
            setPendingGatedAction("addDba");
            setTermsOpen(true);
          }}
        />
      )}

      {/* Matches the ticket detail layout exactly (pages/tickets/Ticket.jsx):
          a fixed 320px rail, main column takes the remainder. The old
          2/3-1/3 split made the rail grow with the viewport. */}
      <div className="grid grid-cols-1 items-start gap-6 lg:grid-cols-[1fr_320px]">
        {/* Main Content */}
        <div className="space-y-6">
          {/* SOUND-643 Phase 1 — schema-driven. Every field the schema declares
              renders, including blanks; grouping comes from the schema itself.
              Replaces hand-written <InfoField> lists that had drifted to 21 of 55
              scalar fields. */}
          <RecordSections
            schema={schema}
            application={application}
            only={["businessInfo"]}
            applicationId={companyId}
            editContext={{ isInternal: isAdmin, isPartner: partner, status: currentStatus }}
            onSaved={refetch}
          />
          {/* DBAs. Header matches the schema-driven sections above — chevron,
              plain title, count badge — rather than the brand-tinted icon tile
              the older cards used. "Add DBA" cannot nest inside the toggle
              button, so the toggle wraps only the title. */}
          {(dbas.length > 0 || canManageDbas) && (
            <div className="surface rounded-xl ring-1 ring-border">
              <div className="flex items-center justify-between gap-3 px-6 py-4">
                <button
                  type="button"
                  onClick={() => setDbasOpen((v) => !v)}
                  aria-expanded={dbasOpen}
                  className="flex min-w-0 items-center gap-2 text-left"
                >
                  {dbasOpen ? (
                    <ChevronDown className="h-4 w-4 shrink-0 text-muted-foreground" />
                  ) : (
                    <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground" />
                  )}
                  <h3 className="truncate text-base font-semibold text-foreground">
                    URLs &amp; DBAs
                  </h3>
                  <span className="badge badge-info shrink-0">{dbas.length}</span>
                </button>
                {canAddDba && (
                  <button
                    type="button"
                    className="btn btn-outline shrink-0 gap-2"
                    onClick={handleAddDbaClick}
                    title="Add another DBA to this application"
                  >
                    <Plus className="h-4 w-4" />
                    Add DBA
                  </button>
                )}
              </div>
              <div
                className={
                  dbasOpen
                    ? "space-y-6 border-t border-border px-6 pb-6 pt-5"
                    : "hidden"
                }
              >
                {dbas.length === 0 ? (
                  <p className="text-sm text-muted-foreground">
                    No DBAs on this application yet.
                  </p>
                ) : (
                  dbas.map((dba, i) => (
                    <div
                      key={dba.dbaId || i}
                      className={i > 0 ? "border-t border-border pt-6" : ""}
                    >
                      <DbaRowEditor
                        applicationId={companyId}
                        dba={dba}
                        schema={schema}
                        canEdit={canManageDbas}
                        onEditAttempt={handleDbaEditAttempt}
                        onSaved={refetch}
                      />
                    </div>
                  ))
                )}
              </div>
            </div>
          )}

          {/* Owners, Banking and Processing.
              Banking and Processing were previously in the 1/3 SIDEBAR, which is
              survivable while Processing renders 5 of its 26 fields and not at 26.
              Moved into the main column; the sidebar keeps the Actions panel. */}
          <RecordSections
            schema={schema}
            application={application}
            only={["owners", "bankingInfo", "processingInfo"]}
            applicationId={companyId}
            editContext={{ isInternal: isAdmin, isPartner: partner, status: currentStatus }}
            onSaved={refetch}
          />
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Actions — every action on this record lives here.
              Previously only status transitions did, and it rendered solely when
              `isAdmin && validTransitions.length`, so Download and Create MID had
              to sit in a separate top bar. Download is available to any viewer,
              so the panel now always renders. */}
          <section className="surface p-4">
            <h3 className="mb-3 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              <Settings className="h-3.5 w-3.5" />
              Actions
            </h3>
            {/* ONE flat list. Ordering carries the meaning that the divider
                used to — see utilities/registrationActions.js for the two rules
                that earn their place (reopen last, Create MID above Download).
                Reads top-to-bottom as "what would you most likely do next". */}
            <div className="space-y-1">
              {/*
                SOUND-618: the inline reason panel was replaced by
                ApplicationStatusDialog (rendered below). Every transition now
                confirms — previously only reason-requiring ones did, so
                "Approve" fired irreversibly on one click.
              */}
              {isAdmin &&
                forwardTransitions.map((targetStatus) => {
                  const config = actionConfigFor(targetStatus);
                  const Icon = config.icon;
                  return (
                    <button
                      key={targetStatus}
                      onClick={() => setPendingStatus(targetStatus)}
                      disabled={changingStatus}
                      className={`btn ${config.className} w-full justify-start gap-2`}
                    >
                      {Icon && <Icon className={`h-4 w-4 ${config.iconClass || ""}`} />}
                      <span>{config.label}</span>
                    </button>
                  );
                })}

              {canEdit && (
                <button
                  onClick={handleEditClick}
                  className="btn btn-outline w-full justify-start gap-2"
                  title="Edit this draft registration"
                >
                  <Edit3 className="h-4 w-4" />
                  <span>Edit draft</span>
                </button>
              )}

              {/* Above Download deliberately: once a registration is approved,
                  applying to banks is the next step, and with Reopen demoted
                  this becomes the first action in the panel. */}
              {isAdmin && (
                <button
                  onClick={() => navigate(`/companies/${companyId}/create`)}
                  className="btn btn-outline w-full justify-start gap-2"
                >
                  <Plus className="h-4 w-4" />
                  <span>Create MID Application</span>
                </button>
              )}

              <DownloadApplicationButton
                application={application}
                className="btn btn-outline w-full justify-start gap-2"
              />

              {/* Reopen is an UNDO, never a forward step, so it always sits at
                  the bottom — otherwise it would lead the panel on exactly the
                  approved applications where Create MID Application should. */}
              {isAdmin &&
                reopenTransitions.map((targetStatus) => {
                  const config = actionConfigFor(targetStatus);
                  const Icon = config.icon;
                  return (
                    <button
                      key={targetStatus}
                      onClick={() => setPendingStatus(targetStatus)}
                      disabled={changingStatus}
                      className={`btn ${config.className} w-full justify-start gap-2`}
                    >
                      {Icon && <Icon className={`h-4 w-4 ${config.iconClass || ""}`} />}
                      <span>{config.label}</span>
                    </button>
                  );
                })}
            </div>
          </section>

          {/* Application Timeline. Creation and submission are ENTRIES on the
              rail, not a meta block above it — "Created · August 13" beside a
              rail item reading "Application created · 11h ago" states the same
              fact twice in two visual languages. "Last updated" stays as a
              footer because it tracks any field edit, not just a status move,
              so no rail entry covers it. */}
          <section className="surface p-4">
            <h3 className="mb-3 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              <Calendar className="h-3.5 w-3.5" />
              Application Timeline
            </h3>

            <div className="mb-4 flex items-center justify-between gap-2 border-b border-border/60 pb-3">
              <span className="text-xs text-muted-foreground">Current status</span>
              <RegistrationStatusPill status={currentStatus} />
            </div>

            <RegistrationTimeline application={application} />

            {application.updatedAt && (
              <div className="mt-4 border-t border-border/60 pt-3 text-[11px] text-muted-foreground">
                Last updated {formatDate(application.updatedAt)}
              </div>
            )}
          </section>

        </div>
      </div>

    </div>
  );
}
